import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
//
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { accountDiscoverySelectors } from 'app-components/flux/account-discovery';
import { accountsActions, accountsSelectors, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { transactionsActions, transactionsTypes } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';
import { EPOCH_ISO } from 'app-components/utils/date';
import { isDemoMode } from 'app-components/utils/core';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { Field, Form, Formik } from 'formik';

import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogForm from 'components/Dialogs/StdDialogForm';
import AccountTypeSelector from 'components/Formik/AccountTypeSelector';
import AmountField, { ShowSignEnum } from 'components/QuickenControls/AmountField';

import { Divider } from '@mui/material';
import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { PropertyDialogNavigationButton } from 'components/Dialogs/AssetDialog/Property/utils';
import { showRealEstateDialog } from 'components/Dialogs/AssetDialog/actions';
import DateField from 'components/Transactions/DateField';
import UsageSelect from 'components/UsageSelect';

const useStyles = makeStyles()((theme) => ({
  accountName: {
    marginTop: 12,
    width: 550,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  accountTypeFormControl: {
    marginTop: 16,
    width: 550,
  },
  dateField: {
    marginTop: '8px',
  },
  onField: {
    marginRight: 12,
    marginLeft: 12,
    width: 24,
  },
  zillowInputDirective: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: '16px',
  },
  toZillowInputButton: {
    padding: '8px 0',
    textTransform: 'none',
  },
}));

const NonConnectedAccount = (props: {
  onClose: (event: any, reason: string) => void;
  scope: string;
  defaultVals: {
    accountName?: string;
    accountType?: accountsTypes.AccountTypeNode;
    statementDate?: DateTime;
    statementBalance?: string;
  };
  trackingProperties: object;
}) => {
  const { onClose, scope, trackingProperties, defaultVals = {} } = props;
  const dispatch = useDispatch();

  const isSubmitting = useSelector((state) => accountDiscoverySelectors.getIsSubmitting(state, { scope }));
  const accountsById = useSelector(accountsSelectors.getAccountsById);
  const accountNames = useMemo(() => accountsById.map((acct: accountsTypes.Account) => acct.name), [accountsById]);
  const defaultBusiness = useSelector(businessSelectors.getDefaultBusiness);
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);

  const [isSubmittingManual, setIsSubmittingManual] = useState(false);

  const { classes } = useStyles();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  // @ts-expect-error FIXME TS7006
  const handleSubmit = (values, _actions) => {
    if (isDemoMode()) {
      setShowTooltip(true);
      return;
    }
    const isBusiness = businessEnabled && values.usage && values.usage !== 'PERSONAL';
    const accountParams = {
      clientId: uuidv4().toUpperCase(),

      name: values.accountName,
      subType: values.accountType.qcsSubType,
      type: values.accountType.qcsType,
      usageType: isBusiness ? 'BUSINESS' : 'PERSONAL',
      businessId: isBusiness ? values.usage : null,

      balanceAsOf: 0.0,
      balanceAsOfOn: EPOCH_ISO,
    };

    setIsSubmittingManual(true);

    const accountToUpsert = accountsTypes.mkAccount(values.accountType.qcsType, accountParams);

    const openingBalanceAsOf =
      (values.statementBalance && Number(parseFloat(values.statementBalance).toFixed(2))) || 0.0;
    const openingBalanceAsOfOn = values.statementDate?.toISODate?.() || EPOCH_ISO;

    new Promise((resolve, reject) => {
      dispatch(accountsActions.batchAccounts([accountToUpsert], { resolve, reject, scope }));
    })
      .then((response: any) => {
        if (openingBalanceAsOf !== 0.0) {
          const amountSign = accountsUtils.balanceSignForType(values?.accountType?.qcsType);
          const accountId = response?.data?.items?.[0]?.resource?.id;
          if (accountId) {
            const newTxn = transactionsTypes.mkCashFlowTransaction({
              accountId,
              payee: 'Opening Balance',
              coa: {
                type: 'BALANCE_ADJUSTMENT',
                id: '0',
              },
              state: 'RECONCILED',
              isReviewed: true,
              amount: amountSign === -1 ? -Math.abs(openingBalanceAsOf) : openingBalanceAsOf,
              postedOn: openingBalanceAsOfOn,
              clientId: uuidv4().toUpperCase(),
              isExcludedFromReports: true,
              isExcludedFromF2S: true,
            });
            dispatch(transactionsActions.batchTransactions([newTxn], { context: 'addAccounts', scope }));
          }
        }
      })
      .catch((_error) => {
        setIsSubmittingManual(false);
      });

    tracker.track(tracker.events.addFIComplete, {
      ...trackingProperties,
      accounts_added: 1,
      accounts_ignored: 0,
      accounts_linked: 0,
      accounts_changed_name: 0,
      accounts_changed_type: 0,
      accounts_changed_subtype: 0,
      accounts_already_connected: 0,
      numberPersonal: !isBusiness ? 1 : 0,
      numberBusiness: isBusiness ? 1 : 0,
    });
  };

  // @ts-expect-error FIXME TS7006
  const renderForm = (formikBag) => {
    const { handleSubmit: formikHandleSubmit, errors, touched, isValid, values } = formikBag;
    let statementBalanceLabel = 'Statement Ending Balance';
    let showDate = true;

    const { accountType } = values;
    if (accountType) {
      statementBalanceLabel = 'Opening Balance';
      // hide date and update label for asset accounts
      if (accountType.id === 'OTHER_ASSET' || accountType.id === 'REAL_ESTATE' || accountType.id === 'VEHICLE') {
        showDate = false;
        statementBalanceLabel = 'Value';
      }
    }

    const error = errors.accountName && touched.accountName ? errors.accountName : null;

    return (
      <StdDialogForm component={Form} onSubmit={formikHandleSubmit}>
        <StdDialogContent id='non_connected_account_dialog'>
          <Typography variant='titleRegular' className={classes.subtitle}>
            Start by choosing the manual account type
          </Typography>
          <div className={classes.accountTypeFormControl}>
            <AccountTypeSelector
              fullWidth
              id='manual_account_type'
              name='accountType'
              showManualTypes
              onChange={(value: accountsTypes.AccountTypeNode) => {
                if (value?.business) {
                  formikBag.setFieldValue('usage', defaultBusiness?.id);
                } else {
                  formikBag.setFieldValue('usage', '');
                }
              }}
            />
          </div>
          <Divider sx={{ m: '24px 0' }} />
          {values.accountType && (
            <Typography variant='titleRegular' className={classes.subtitle}>
              Fill in the rest of the account details
            </Typography>
          )}
          {/* if there's no default business / no account type / business disabled, we don't the usage type field */}
          {values.accountType && businessEnabled && defaultBusiness && (
            <Field name='usage' label='Usage' variant='filled' className={classes.accountName}>
              {/*
              // @ts-expect-error FIXME TS7031 */}
              {({ field }) => (
                <UsageSelect
                  {...field}
                  disabled={false}
                  iconOnly={false}
                  nameAndIcon
                  className={classes.accountName}
                  onChange={(_event, newValue) => formikBag.setFieldValue('usage', newValue)}
                  fallback=''
                  label='Usage Type'
                />
              )}
            </Field>
          )}
          {values.accountType && (
            <Field
              name='accountName'
              label='Account Name'
              variant='filled'
              className={classes.accountName}
              inputProps={{
                id: 'manual_account_name',
              }}
              // @ts-expect-error FIXME TS7006
              validate={(value) => {
                const alreadyInUse = accountNames.includes(value);
                const invalidName = accountsUtils.isInvalidName(value);

                if (value && value.length > 0 && !invalidName && !alreadyInUse) {
                  return undefined;
                }
                if (invalidName) {
                  return 'invalid account name';
                }
                if (alreadyInUse) {
                  return 'Account name already in use';
                }
                return 'required';
              }}
            >
              {/*
             // @ts-expect-error FIXME TS7031 */}
              {({ field }) => (
                <TextField
                  {...field}
                  className={classes.accountName}
                  id='manual_account_name-field'
                  label='Account Name'
                  InputProps={{
                    autoComplete: 'off',
                    maxLength: 64,
                    inputProps: {
                      maxLength: 64,
                    },
                  }}
                  variant='filled'
                  margin='normal'
                  fullWidth
                  error={!!error}
                  helperText={error}
                />
              )}
            </Field>
          )}
          {values.accountType && values.accountType.qcsType !== 'INVESTMENT' && (
            <Box
              alignItems='center'
              display='flex'
              justifyContent={showDate ? 'space-between' : 'normal'}
              mt={2}
              width='552px'
            >
              <Field name='statementBalance'>
                {/*
                 // @ts-expect-error FIXME TS7031 */}
                {({ field }) => (
                  <AmountField
                    {...field}
                    variant='filled'
                    showSign={ShowSignEnum.NEVER}
                    hideCurrencyString
                    style={{ width: 252 }}
                    editable
                    omitCents
                    currencySymbol='USD'
                    amountType='amount'
                    id='schedtxn-amount'
                    marginProp={'dense'}
                    textFieldVariant={'filled'}
                    label={statementBalanceLabel}
                    autoComplete='one-time-code'
                    InputProps={{ id: 'manual_ending_balance' }}
                  />
                )}
              </Field>

              {showDate && <Typography className={classes.onField}>On</Typography>}

              {showDate && (
                <Field
                  name='statementDate'
                  // @ts-expect-error FIXME TS7006
                  validate={(value) => {
                    if (value?.isValid) {
                      return value.toISODate?.() <= DateTime.now().toISODate() ? '' : 'date cannot be in the future';
                    }
                    return 'invalid';
                  }}
                >
                  {/*
                   // @ts-expect-error FIXME TS7031 */}
                  {({ field: { value } }) => (
                    <DateField
                      id='manual_ending_date'
                      className={classes.dateField}
                      date={value}
                      onChange={(statementDate: DateTime<true>) =>
                        formikBag.setFieldValue('statementDate', statementDate)
                      }
                      maxDate={DateTime.now()}
                      error={Boolean(errors.statementDate)}
                      helperText={errors.statementDate}
                      allowInvalid
                    />
                  )}
                </Field>
              )}
            </Box>
          )}
          {values.accountType && values.accountType.id === 'REAL_ESTATE' && (
            <PropertyDialogNavigationButton
              containerClass={classes.zillowInputDirective}
              styleClass={classes.toZillowInputButton}
              handleDialogNavigation={() => {
                dispatch(showRealEstateDialog());
                onClose(null, '');
              }}
              icon={<ArrowForwardIcon />}
              buttonText={'Add with Zillow instead'}
              description={'Link your property to its Zillow value'}
              textVariant={'captionRegular'}
            />
          )}
        </StdDialogContent>
        <StdDialogActions
          primary={{
            children: 'Continue',
            disabled: !isValid || isSubmittingManual || isSubmitting || !values.accountType || !values.accountName,
            id: 'manual_done_btn',
            tooltipText: 'Not available in Test Drive.',
            showTooltip,
          }}
        />
      </StdDialogForm>
    );
  };

  const initialVals = { accountName: '', statementDate: DateTime.now(), statementBalance: '0', ...defaultVals };
  return (
    <Formik initialValues={initialVals} validateOnBlur onSubmit={(values, actions) => handleSubmit(values, actions)}>
      {renderForm}
    </Formik>
  );
};

export default NonConnectedAccount;
